<template>
  <div class="rj-page-card">
    <div class="rj-page-card__title">退货合同</div>
    <div class="rj-page-card__content">
      <div class="rj-row">
        <div v-for="field in fields" :key="field.label" :class="field.wide ? 'rj-col-66' : 'rj-col-3'">
          <div class="rj-baseinfo">
            <div class="rj-baseinfo__label">{{ field.label }}：</div>
            <div class="rj-baseinfo__item">{{ displayFieldValue(field) }}</div>
          </div>
        </div>
        <div class="rj-col-3">
          <div class="rj-baseinfo">
            <div class="rj-baseinfo__label"><span class="required">*</span>场景分类：</div>
            <div class="rj-baseinfo__item">
              <el-select :value="value.scenarioCategoryKey" :disabled="disabled" placeholder="请选择场景分类" @input="changeCategory">
                <el-option v-for="item in displayCategoryOptions" :key="item.categoryKey" :label="item.categoryValue" :value="item.categoryKey" />
              </el-select>
            </div>
          </div>
        </div>
        <div class="rj-col-66">
          <div class="rj-baseinfo">
            <div class="rj-baseinfo__label">补充条款：</div>
            <div class="rj-baseinfo__item">
              <el-input :value="value.supplementalTerm" :disabled="disabled" type="textarea" maxlength="500" show-word-limit @input="update('supplementalTerm', $event)" />
            </div>
          </div>
        </div>
        <div class="rj-col-66 mt-2">
          <div class="rj-baseinfo">
            <div class="rj-baseinfo__label">备注：</div>
            <div class="rj-baseinfo__item">
              <el-input :value="value.remarks" :disabled="disabled" type="textarea" maxlength="500" show-word-limit @input="update('remarks', $event)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { displayReasonType } from '@/constant';

export default {
  name: 'SalesBorrowBaseInfo',
  props: {
    info: { type: Object, default: () => ({}) },
    value: { type: Object, required: true },
    categoryOptions: { type: Array, default: () => [] },
    disabled: Boolean
  },
  data () {
    return {
      // keys 按「原合同详情 -> 退货明细 -> 申请单」三段来源依次回退取值；
      // 后端各来源的 Jackson 序列化键不同（r 前缀字段会被压成 rxxx），故每项都列出全部候选
      fields: [
        { label: '项目名称', keys: ['projectName', 'rprojectName', 'rProjectName'], wide: true },
        { label: '订单日期', keys: ['createdTime', 'rcreatedTime', 'rCreatedTime'] },
        { label: '电子商务合同编号', keys: ['newContractNum', 'rnewContractNum', 'rNewContractNum'] },
        { label: '原订单合同编号', keys: ['contractNum', 'oldContractNum', 'roldContractNum', 'rOldContractNum'] },
        { label: '订单分类', keys: ['orderType', 'rorderType', 'rOrderType'] },
        { label: '退换货原因', keys: ['reasonTypeName', 'rreasonType', 'rReasonType'] },
        { label: '供货单位（甲方）', keys: ['supplyUnitName', 'supplyUnit', 'rsupplyUnit'], wide: true },
        { label: '供货单位地址', keys: ['supplyAddress', 'rsupplyAddress'], wide: true },
        { label: '供货单位电话（甲方）', keys: ['supplyPhone', 'rsupplyPhone'] },
        { label: '订货单位（乙方）', keys: ['orderUnitName', 'rorderUnitName', 'rOrderUnitName'], wide: true },
        { label: '订货单位电话（乙方）', keys: ['orderUnitTel', 'rorderUnitTel', 'rOrderUnitTel'] },
        { label: '最终客户', keys: ['finalCustomerName', 'rfinalCustomerName'] },
        { label: '客户签约类型', keys: ['distributorName', 'rdistributorName'] },
        { label: '区域', keys: ['salesArea', 'rsalesArea'] },
        { label: '首级集成商名称', keys: ['integratorName', 'rintegratorName'] },
        { label: '首级集成商类型', keys: ['integratorSignType', 'rintegratorSignType'] },
        { label: '区域商务专员', keys: ['assistantName', 'rassistantName', 'rAssistantName'] },
        { label: '售前名称', keys: ['preSalesPersonName', 'rpreSalesPersonName'] },
        { label: '外销名称', keys: ['salesPersonName', 'rsalesPersonName'] },
        { label: '部门', keys: ['salesPersonDeptName', 'rsalesPersonDeptName'] },
        { label: '末级集成商名称', keys: ['finalIntegratorsName', 'rfinalIntegratorsName'] },
        { label: '末级集成商类型', keys: ['finalIntegratorsType', 'rfinalIntegratorsType'] },
        { label: '计量集成商名称', keys: ['metrologyIntegratorName', 'rmetrologyIntegratorName'] },
        { label: '计量集成商类型', keys: ['metrologyIntegratorType', 'rmetrologyIntegratorType'] },
        { label: '细分市场', keys: ['marketSegmentName', 'rmarketSegmentName'] },
        { label: '应用场合', keys: ['applicationScenariosName', 'rapplicationScenariosName'] },
        { label: '应用场景', keys: ['applicationSceneName', 'rapplicationSceneName'] },
        { label: '返利退还金额', keys: ['refundRebate', 'rrefundRebate', 'rRefundRebate'] },
        { label: '净销售额', keys: ['netSales', 'rnetSales', 'rNetSales'] }
      ]
    };
  },
  computed: {
    displayCategoryOptions () {
      if (this.categoryOptions.length || !this.value.scenarioCategoryKey) return this.categoryOptions;
      return [{
        categoryKey: this.value.scenarioCategoryKey,
        categoryValue: this.value.scenarioCategoryValue
      }];
    }
  },
  methods: {
    /**
     * 取字段展示值。
     *
     * D8：退换货原因字段的候选 key 含原合同落库值（如“产品发运错误”），
     * 需统一经 displayReasonType 映射后再展示，避免同页出现旧名称。
     *
     * @param {Object} field 字段定义（含 label 与 keys）
     * @returns {string} 展示值
     */
    displayFieldValue (field) {
      const raw = this.displayValue(...field.keys);
      return field.label === '退换货原因' ? displayReasonType(raw) : raw;
    },
    displayValue (...keys) {
      const key = keys.find(item => this.info[item] !== undefined && this.info[item] !== null);
      return key ? this.info[key] : '-';
    },
    changeCategory (key) {
      const selected = this.displayCategoryOptions.find(item => item.categoryKey === key);
      this.$emit('input', { ...this.value, scenarioCategoryKey: key, scenarioCategoryValue: selected ? selected.categoryValue : '' });
    },
    update (key, value) {
      this.$emit('input', { ...this.value, [key]: value });
    }
  }
};
</script>

<style lang="scss" scoped>
.rj-col-66 {
  float: left;
  box-sizing: border-box;
  width: 66.6666%;
}

.required {
  color: #f56c6c;
  margin-right: 4px;
}
</style>
